import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { toastSuccess, toastError } from '../../../utils/toast';
import API_BASE_URL from '../../../config/api';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const inputStyle = { width: '100%', minHeight: '48px', padding: '0 20px', background: '#FBF9D9', border: 'none', borderRadius: '999px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none' };
const textareaStyle = { width: '100%', padding: '14px 20px', background: '#FBF9D9', border: 'none', borderRadius: '20px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none', resize: 'vertical' };
const labelStyle = { display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' };
const errorStyle = { margin: '6px 0 0', fontSize: '12.5px', color: '#A32C0F' };

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    style={{ border: 'none', cursor: 'pointer', width: '50px', height: '28px', borderRadius: '999px', padding: '3px', display: 'flex', background: checked ? '#72B744' : 'rgba(5,16,4,0.22)', transition: 'background 180ms ease', flexShrink: 0 }}
  >
    <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', transform: checked ? 'translateX(22px)' : 'translateX(0)', transition: 'transform 180ms ease' }} />
  </button>
);

const CheckRow = ({ id, checked, onChange, children }) => (
  <label htmlFor={id} className="flex items-start gap-3" style={{ cursor: 'pointer' }}>
    <button
      type="button"
      id={id}
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{ flex: 'none', marginTop: '2px', cursor: 'pointer', width: '22px', height: '22px', borderRadius: '7px', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, background: checked ? PRIMARY : 'transparent', color: checked ? '#FBF9D9' : 'transparent', border: checked ? 'none' : '2px solid rgba(5,16,4,0.22)' }}
    >
      ✓
    </button>
    <span style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.78)' }}>{children}</span>
  </label>
);

const DishModal = ({ isOpen, onClose, dish, onSuccess, categories, groceryShopMode = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    is_available: true,
    hidden_from_marketplace: false,
    is_grocery_product: false,
    catering_only: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // URLs d'images par défaut sur un CDN
  const defaultImages = [
    'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1544025162-d76694265947?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=400&h=300&fit=crop'
  ];

  useEffect(() => {
    if (dish) {
      const imageUrl = dish.dish_image_url || dish.image_url || '';
      setFormData({
        name: dish.name || '',
        description: dish.description || '',
        price: dish.price ? dish.price.toString() : '',
        category_id: dish.category_id ? dish.category_id.toString() : '',
        image_url: imageUrl,
        is_available: dish.is_available !== false,
        hidden_from_marketplace: dish.hidden_from_marketplace || false,
        is_grocery_product: dish.is_grocery_product || false,
        catering_only: dish.catering_only || false,
      });
      setImagePreview(imageUrl);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: '',
        image_url: '',
        is_available: true,
        hidden_from_marketplace: false,
        is_grocery_product: groceryShopMode,
      });
      setImagePreview('');
    }
    setErrors({});
  }, [dish, isOpen, groceryShopMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Le prix doit être supérieur à 0';
    }
    if (!formData.category_id) newErrors.category_id = 'La catégorie est requise';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError('Veuillez corriger les champs : nom, prix et catégorie sont obligatoires.');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        category_id: parseInt(formData.category_id),
        hidden_from_marketplace: !!formData.hidden_from_marketplace,
        is_grocery_product: groceryShopMode ? true : !!formData.is_grocery_product,
        catering_only: !!formData.catering_only,
      };

      if (dish) {
        await onSuccess(dish.id, submitData);
        toastSuccess('Plat mis à jour avec succès.');
      } else {
        await onSuccess(submitData);
        toastSuccess('Plat créé avec succès.');
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde' });
      toastError(error.response?.data?.detail || 'Erreur lors de la sauvegarde du plat.');
    } finally {
      setLoading(false);
    }
  };

  const selectRandomImage = () => {
    const randomImage = defaultImages[Math.floor(Math.random() * defaultImages.length)];
    setFormData(prev => ({ ...prev, image_url: randomImage }));
    setImagePreview(randomImage);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation de base
    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, image: 'Le fichier doit être une image' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setErrors({ ...errors, image: 'L\'image ne doit pas dépasser 5MB' });
      return;
    }

    try {
      setUploadingImage(true);
      setErrors({ ...errors, image: null });

      // Créer FormData pour l'upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'dishes');

      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/management/upload/image`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();

      // Mettre à jour le form avec l'URL uploadée
      setFormData(prev => ({ ...prev, image_url: result.image_url }));
      setImagePreview(result.image_url);

    } catch (error) {
      console.error('Erreur upload:', error);
      setErrors({ ...errors, image: 'Erreur lors de l\'upload de l\'image' });
    } finally {
      setUploadingImage(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>
            {dish
              ? groceryShopMode
                ? 'Modifier le produit'
                : 'Modifier le plat'
              : groceryShopMode
                ? 'Ajouter un produit épicerie'
                : 'Ajouter un plat'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '26px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {errors.general && (
            <div style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '16px', padding: '12px 16px', fontSize: '13.5px' }}>
              {errors.general}
            </div>
          )}

          {/* Nom */}
          <div>
            <label style={labelStyle}>Nom du plat *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              style={inputStyle}
              placeholder="Ex: Salade César"
            />
            {errors.name && <p style={errorStyle}>{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={textareaStyle}
              placeholder="Décrivez votre plat..."
            />
          </div>

          {/* Prix et Catégorie */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Prix (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                style={inputStyle}
                placeholder="15.99"
              />
              {errors.price && <p style={errorStyle}>{errors.price}</p>}
            </div>

            <div>
              <label style={labelStyle}>Catégorie *</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                style={inputStyle}
              >
                <option value="">Sélectionner une catégorie</option>
                {categories && Array.isArray(categories) && categories
                  .filter(category => category && typeof category === 'object' && category.id && category.name)
                  .map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name || 'Sans nom'}
                    </option>
                  ))}
              </select>
              {errors.category_id && <p style={errorStyle}>{errors.category_id}</p>}
            </div>
          </div>

          {/* Image */}
          <div>
            <label style={labelStyle}>Image du plat</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="flex gap-2.5 flex-wrap">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ flex: '1 1 200px', fontSize: '13px', color: 'rgba(5,16,4,0.7)' }}
                />
                <button
                  type="button"
                  onClick={selectRandomImage}
                  disabled={uploadingImage}
                  className="inline-flex items-center gap-2"
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', minHeight: '42px', padding: '0 18px', borderRadius: '999px', opacity: uploadingImage ? 0.5 : 1 }}
                >
                  <ImageIcon size={15} />
                  Image par défaut
                </button>
              </div>

              {uploadingImage && (
                <div className="flex items-center gap-2" style={{ fontSize: '13px', color: PRIMARY }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '999px', border: '2px solid rgba(5,70,37,0.25)', borderTopColor: PRIMARY }} className="animate-spin" />
                  Upload en cours...
                </div>
              )}

              {errors.image && <p style={errorStyle}>{errors.image}</p>}

              {(formData.image_url || imagePreview) && (
                <div style={{ borderRadius: '18px', overflow: 'hidden' }}>
                  <img
                    src={imagePreview || formData.image_url}
                    alt="Aperçu"
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Disponibilité */}
          <div className="flex items-center gap-3">
            <Toggle checked={formData.is_available} onChange={(v) => setFormData(prev => ({ ...prev, is_available: v }))} />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>{groceryShopMode ? 'Produit disponible' : 'Plat disponible'}</span>
          </div>

          {!groceryShopMode && (
            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ margin: 0, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Visibilité</p>
              <CheckRow
                id="hidden_from_marketplace"
                checked={formData.hidden_from_marketplace}
                onChange={(v) => setFormData((prev) => ({ ...prev, hidden_from_marketplace: v }))}
              >
                Masquer sur le marketplace (nomke) — le plat reste visible ici dans la gestion et peut
                rester dans vos menus pour usage interne / commandes liées.
              </CheckRow>
              <CheckRow
                id="is_grocery_product"
                checked={formData.is_grocery_product}
                onChange={(v) => setFormData((prev) => ({ ...prev, is_grocery_product: v }))}
              >
                Produit d&apos;épicerie — géré aussi dans la section Épicerie du management.
              </CheckRow>
              <CheckRow
                id="catering_only"
                checked={formData.catering_only}
                onChange={(v) => setFormData((prev) => ({ ...prev, catering_only: v }))}
              >
                Visible uniquement pour les services traiteur — exclu du menu public du restaurant.
              </CheckRow>
            </div>
          )}

          {groceryShopMode && (
            <div style={{ background: '#F1F8E8', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ margin: 0, fontSize: '13.5px', color: '#37702C' }}>
                Produit <strong>épicerie</strong> — visible sur le marketplace sauf option ci-dessous.
              </p>
              <CheckRow
                id="hidden_grocery_shop"
                checked={formData.hidden_from_marketplace}
                onChange={(v) => setFormData((prev) => ({ ...prev, hidden_from_marketplace: v }))}
              >
                Masquer sur le marketplace public (visible uniquement ici en gestion)
              </CheckRow>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 flex-wrap" style={{ marginTop: '8px', paddingTop: '18px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 22px', borderRadius: '999px' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Enregistrement...' : (dish ? 'Modifier' : 'Ajouter')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DishModal;